/**
 * Das Strecken-Objektblatt (Einrichten-Neubau P3) — der Ersatz für den
 * Härtefall der alten Verwaltung (Strecken-Detailseite mit zwei Speicher-
 * Knöpfen und Emoji-Regelmatrix in zwei konkurrierenden Editoren).
 *
 * Aufbau wie jedes Objektblatt (Kopf mit Zustand, Stammdaten, Verwendung,
 * Verlauf) plus zwei Strecken-Zonen:
 *   - Substrecken: Chips mit Inline-Umbenennen und Außer-Betrieb — kein
 *     Seiten-Pingpong mehr.
 *   - Erfassungsregeln als Sätze mit Herkunft (Standard/global/Strecke);
 *     „für diese Strecke ändern" öffnet zwei Wahlen und speichert auf Knopf.
 *     Geändert wird bewusst nur je Strecke — bei fünf Strecken ist das
 *     ehrlicher als eine unsichtbare Global-Ebene (EINRICHTEN-NEUBAU 5.3a).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Notiz, Zustand } from '../../components/leitstand';

const EINGABE =
  'rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

interface Substrecke {
  id: string;
  name: string;
  isActive: boolean;
}
interface RegelSatz {
  schluessel: string;
  text: string;
  herkunft: 'strecke' | 'global' | 'standard';
}
interface Wahl {
  waescheklasseAbfragen: boolean;
  erlaubteKlassenIds: string[];
  artikelgruppePflicht: boolean;
  erlaubteGruppenIds: string[];
}
interface Regeln {
  saetze: RegelSatz[];
  wahl: Wahl;
  eigeneKonfiguration: boolean;
}
interface Option {
  id: string;
  name: string;
  isActive: boolean;
}

const HERKUNFT_TEXT: Record<RegelSatz['herkunft'], string> = {
  strecke: 'eigene Regel dieser Strecke',
  global: 'geerbt aus der Vorbelegung',
  standard: 'Standard',
};

export default function StreckeBlatt() {
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const istNeu = id === 'neu';

  const [name, setName] = useState('');
  const [aktiv, setAktiv] = useState(true);
  const [laden, setLaden] = useState(!istNeu);
  const [fehler, setFehler] = useState('');
  const [aktionsFehler, setAktionsFehler] = useState('');
  const [gespeichert, setGespeichert] = useState('');
  const [speichert, setSpeichert] = useState(false);

  const [substrecken, setSubstrecken] = useState<Substrecke[]>([]);
  const [subBearbeitet, setSubBearbeitet] = useState<Substrecke | null>(null);
  const [subNeuName, setSubNeuName] = useState('');
  const [subAnlegen, setSubAnlegen] = useState(false);

  const [regeln, setRegeln] = useState<Regeln | null>(null);
  const [regelEditor, setRegelEditor] = useState(false);
  const [wahl, setWahl] = useState<Wahl | null>(null);
  const [klassen, setKlassen] = useState<Option[]>([]);
  const [gruppen, setGruppen] = useState<Option[]>([]);
  const [zurueckFrage, setZurueckFrage] = useState(false);

  const [verwendung, setVerwendung] = useState<
    Array<{ text: string; wert: number; ziel?: string }>
  >([]);
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);
  const [schalterFrage, setSchalterFrage] = useState(false);

  const lade = useCallback(async () => {
    if (istNeu) return;
    setLaden(true);
    setFehler('');
    try {
      const [oRes, rRes, vRes, hRes, subRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/strecken/${id}`, { credentials: 'include' }),
        fetch(`/api/einrichten/strecken/${id}/regeln`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/strecken/${id}/verwendung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/strecken/${id}/verlauf`, { credentials: 'include' }),
        fetch(`/api/admin/lines/${id}`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Die Strecke konnte nicht geladen werden');
      const o = (await oRes.json()).objekt as { name: string; isActive: boolean };
      setName(o.name);
      setAktiv(!!o.isActive);
      if (rRes.ok) setRegeln(await rRes.json());
      if (vRes.ok) setVerwendung((await vRes.json()).zeilen ?? []);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
      if (subRes.ok) {
        const line = await subRes.json();
        setSubstrecken(
          (line.subLines ?? []).map((s: Substrecke) => ({
            id: s.id,
            name: s.name,
            isActive: s.isActive,
          })),
        );
      }
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Strecke konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [id, istNeu]);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Klassen/Gruppen erst beim Öffnen des Regel-Editors
  useEffect(() => {
    if (!regelEditor || klassen.length) return;
    void (async () => {
      const [kRes, gRes] = await Promise.all([
        fetch('/api/einrichten/objekt/waescheklassen', { credentials: 'include' }),
        fetch('/api/einrichten/objekt/artikelgruppen', { credentials: 'include' }),
      ]);
      if (kRes.ok)
        setKlassen(((await kRes.json()).objekte ?? []).filter((o: Option) => o.isActive));
      if (gRes.ok)
        setGruppen(((await gRes.json()).objekte ?? []).filter((o: Option) => o.isActive));
    })();
  }, [regelEditor, klassen.length]);

  const api = async (pfad: string, methode: string, body?: unknown) => {
    setAktionsFehler('');
    const res = await fetch(pfad, {
      method: methode,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const antwort = await res.json().catch(() => null);
    if (!res.ok) throw new Error(antwort?.error || 'Aktion fehlgeschlagen');
    return antwort;
  };

  const speichereName = async () => {
    if (!name.trim()) {
      setAktionsFehler('Bitte einen Namen angeben.');
      return;
    }
    setSpeichert(true);
    try {
      if (istNeu) {
        const antwort = await api('/api/admin/lines', 'POST', { name: name.trim() });
        navigate(antwort?.id ? `/einrichten/strecken/${antwort.id}` : '/einrichten/strecken', {
          replace: true,
        });
        return;
      }
      await api(`/api/admin/lines/${id}`, 'PATCH', { name: name.trim() });
      setGespeichert('✓ Gespeichert');
      setTimeout(() => setGespeichert(''), 2600);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const schalteZustand = async (ziel: boolean) => {
    try {
      await api(`/api/einrichten/objekt/strecken/${id}/zustand`, 'POST', { aktiv: ziel });
      setAktiv(ziel);
      setSchalterFrage(false);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Der Zustand konnte nicht geändert werden');
    }
  };

  const substreckeSpeichern = async () => {
    if (!subBearbeitet) return;
    try {
      await api(`/api/admin/sublines/${subBearbeitet.id}`, 'PATCH', {
        name: subBearbeitet.name.trim(),
      });
      setSubBearbeitet(null);
      void lade();
    } catch (e) {
      setAktionsFehler(
        e instanceof Error ? e.message : 'Substrecke konnte nicht gespeichert werden',
      );
    }
  };

  const substreckeZustand = async (s: Substrecke, ziel: boolean) => {
    try {
      await api(`/api/admin/sublines/${s.id}`, 'PATCH', { isActive: ziel });
      setSubBearbeitet(null);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Substrecke konnte nicht geändert werden');
    }
  };

  const substreckeAnlegen = async () => {
    if (!subNeuName.trim()) return;
    try {
      await api(`/api/admin/lines/${id}/sublines`, 'POST', { name: subNeuName.trim() });
      setSubNeuName('');
      setSubAnlegen(false);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Substrecke konnte nicht angelegt werden');
    }
  };

  const regelnSpeichern = async () => {
    if (!wahl) return;
    try {
      const neu = await api(`/api/einrichten/strecken/${id}/regeln`, 'POST', wahl);
      setRegeln(neu);
      setRegelEditor(false);
      void lade();
    } catch (e) {
      setAktionsFehler(
        e instanceof Error ? e.message : 'Die Regeln konnten nicht gespeichert werden',
      );
    }
  };

  const zurueckAufVorbelegung = async () => {
    try {
      const neu = await api(`/api/einrichten/strecken/${id}/regeln/zurueck-auf-global`, 'POST');
      setRegeln(neu);
      setZurueckFrage(false);
      setRegelEditor(false);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Konnte nicht zurückgesetzt werden');
    }
  };

  const mehrfachWahl = (
    titel: string,
    optionen: Option[],
    gewaehlt: string[],
    setze: (ids: string[]) => void,
  ) => (
    <div className="flex flex-col gap-1.5">
      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
        {titel}
      </span>
      <div className="flex flex-wrap gap-2">
        {optionen.map((o) => {
          const an = gewaehlt.includes(o.id);
          return (
            <button
              key={o.id}
              onClick={() => setze(an ? gewaehlt.filter((x) => x !== o.id) : [...gewaehlt, o.id])}
              className={`rounded-[4px] border px-3 py-1.5 font-zahl text-xs ${
                an
                  ? 'border-akzent bg-akzent-tief text-akzent'
                  : 'border-linie-2 bg-flaeche text-tinte-2'
              }`}
            >
              {o.name}
            </button>
          );
        })}
      </div>
      <span className="text-xs text-tinte-3">Nichts gewählt heißt: alle sind erlaubt.</span>
    </div>
  );

  const jaNein = (frage: string, wert: boolean, setze: (v: boolean) => void) => (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm">{frage}</span>
      <div className="flex gap-1.5">
        <button onClick={() => setze(true)} className={wert ? KNOPF_PRIMAER : KNOPF_LEISE}>
          Ja
        </button>
        <button onClick={() => setze(false)} className={!wert ? KNOPF_PRIMAER : KNOPF_LEISE}>
          Nein
        </button>
      </div>
    </div>
  );

  return (
    <LeitstandRahmen
      titel={istNeu ? 'Strecke anlegen' : name || 'Strecke'}
      benutzer={user?.displayName}
      werkzeuge={
        !istNeu && !laden ? (
          <span className="flex items-center gap-3">
            {aktiv ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
            )}
            <button
              onClick={() => (aktiv ? setSchalterFrage(true) : void schalteZustand(true))}
              className={KNOPF_LEISE}
            >
              {aktiv ? 'Außer Betrieb nehmen' : 'Wieder in Betrieb nehmen'}
            </button>
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <Link to="/einrichten/strecken" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Strecken
        </Link>

        {schalterFrage && (
          <div className="flex flex-wrap items-center gap-3 rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3">
            <span className="text-[13.5px] text-tinte-2">
              Außer Betrieb nimmt die Strecke samt Substrecken aus der Auswahl an den Stationen.
              Erfasste Einträge bleiben unverändert und auswertbar.
            </span>
            <button onClick={() => void schalteZustand(false)} className={KNOPF_PRIMAER}>
              Außer Betrieb nehmen
            </button>
            <button onClick={() => setSchalterFrage(false)} className={KNOPF_LEISE}>
              Abbrechen
            </button>
          </div>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Strecke …</p>
        )}

        {!laden && !fehler && (
          <>
            <Karte titel="Stammdaten" marke="ein Speichern, eine Rückmeldung">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Name *
                    </span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`${EINGABE} w-full`}
                    />
                  </label>
                  {!istNeu && (
                    <div className="flex flex-col gap-1.5">
                      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                        Substrecken
                      </span>
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {substrecken.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => {
                              setSubBearbeitet({ ...s });
                              setSubAnlegen(false);
                            }}
                            className={`rounded-[4px] border px-3 py-1.5 font-zahl text-xs ${
                              s.isActive
                                ? 'border-linie-2 bg-flaeche-2 text-tinte-2'
                                : 'border-linie bg-flaeche text-tinte-3 line-through'
                            }`}
                          >
                            {s.name}
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            setSubAnlegen(true);
                            setSubBearbeitet(null);
                          }}
                          className={KNOPF_LEISE}
                        >
                          + Substrecke
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {subBearbeitet && (
                  <div className="flex flex-wrap items-center gap-2 rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2.5">
                    <input
                      value={subBearbeitet.name}
                      onChange={(e) => setSubBearbeitet({ ...subBearbeitet, name: e.target.value })}
                      className={EINGABE}
                    />
                    <button onClick={() => void substreckeSpeichern()} className={KNOPF_PRIMAER}>
                      Umbenennen
                    </button>
                    {subBearbeitet.isActive ? (
                      <button
                        onClick={() => void substreckeZustand(subBearbeitet, false)}
                        className={KNOPF_LEISE}
                      >
                        Außer Betrieb nehmen
                      </button>
                    ) : (
                      <button
                        onClick={() => void substreckeZustand(subBearbeitet, true)}
                        className={KNOPF_LEISE}
                      >
                        Wieder in Betrieb nehmen
                      </button>
                    )}
                    <button onClick={() => setSubBearbeitet(null)} className={KNOPF_LEISE}>
                      Schließen
                    </button>
                  </div>
                )}

                {subAnlegen && (
                  <div className="flex flex-wrap items-center gap-2 rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2.5">
                    <input
                      value={subNeuName}
                      onChange={(e) => setSubNeuName(e.target.value)}
                      placeholder="Name der Substrecke …"
                      className={EINGABE}
                      autoFocus
                    />
                    <button onClick={() => void substreckeAnlegen()} className={KNOPF_PRIMAER}>
                      Anlegen
                    </button>
                    <button
                      onClick={() => {
                        setSubAnlegen(false);
                        setSubNeuName('');
                      }}
                      className={KNOPF_LEISE}
                    >
                      Abbrechen
                    </button>
                  </div>
                )}

                {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void speichereName()}
                    disabled={speichert}
                    className={KNOPF_PRIMAER}
                  >
                    {istNeu ? 'Anlegen' : 'Speichern'}
                  </button>
                  {gespeichert && (
                    <span className="font-zahl text-[13px] text-lauf">{gespeichert}</span>
                  )}
                </div>
              </div>
            </Karte>

            {!istNeu && regeln && (
              <Karte titel="Erfassungsregeln" marke="Sätze mit Herkunft — ein Ort, ein Editor">
                <div className="flex flex-col gap-1">
                  {regeln.saetze.map((satz, i) => (
                    <div
                      key={`${satz.schluessel}-${i}`}
                      className={`flex flex-wrap items-baseline gap-2 px-1 py-2.5 text-[14.5px] ${
                        i < regeln.saetze.length - 1 ? 'border-b border-linie' : ''
                      }`}
                    >
                      <span>{satz.text}</span>
                      <span
                        className={`text-xs ${satz.herkunft === 'strecke' ? 'text-akzent' : 'text-tinte-3'}`}
                      >
                        {HERKUNFT_TEXT[satz.herkunft]}
                      </span>
                    </div>
                  ))}
                </div>

                {!regelEditor ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setWahl({ ...regeln.wahl });
                        setRegelEditor(true);
                        setZurueckFrage(false);
                      }}
                      className={KNOPF_LEISE}
                    >
                      Für diese Strecke ändern
                    </button>
                    {regeln.eigeneKonfiguration && !zurueckFrage && (
                      <button onClick={() => setZurueckFrage(true)} className={KNOPF_LEISE}>
                        Eigene Regeln entfernen
                      </button>
                    )}
                    {zurueckFrage && (
                      <>
                        <span className="self-center text-[13px] text-tinte-2">
                          Die Strecke folgt dann wieder der Vorbelegung.
                        </span>
                        <button
                          onClick={() => void zurueckAufVorbelegung()}
                          className={KNOPF_PRIMAER}
                        >
                          Ja, entfernen
                        </button>
                        <button onClick={() => setZurueckFrage(false)} className={KNOPF_LEISE}>
                          Abbrechen
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  wahl && (
                    <div className="mt-4 flex flex-col gap-4 rounded-[4px] border border-linie-2 bg-flaeche-2 p-4">
                      {jaNein('Wird die Wäscheklasse abgefragt?', wahl.waescheklasseAbfragen, (v) =>
                        setWahl({ ...wahl, waescheklasseAbfragen: v }),
                      )}
                      {wahl.waescheklasseAbfragen &&
                        mehrfachWahl(
                          'Wählbare Wäscheklassen',
                          klassen,
                          wahl.erlaubteKlassenIds,
                          (ids) => setWahl({ ...wahl, erlaubteKlassenIds: ids }),
                        )}
                      {jaNein('Ist die Artikelgruppe Pflicht?', wahl.artikelgruppePflicht, (v) =>
                        setWahl({ ...wahl, artikelgruppePflicht: v }),
                      )}
                      {wahl.artikelgruppePflicht &&
                        mehrfachWahl(
                          'Wählbare Artikelgruppen',
                          gruppen,
                          wahl.erlaubteGruppenIds,
                          (ids) => setWahl({ ...wahl, erlaubteGruppenIds: ids }),
                        )}
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => void regelnSpeichern()} className={KNOPF_PRIMAER}>
                          Für diese Strecke speichern
                        </button>
                        <button onClick={() => setRegelEditor(false)} className={KNOPF_LEISE}>
                          Abbrechen
                        </button>
                      </div>
                      <p className="text-xs text-tinte-3">
                        Gilt ab dem nächsten Aufruf an den Stationen. Bereits Erfasstes bleibt
                        unverändert; die Änderung erscheint im Verlauf.
                      </p>
                    </div>
                  )
                )}
              </Karte>
            )}

            {!istNeu && (
              <Karte titel="Verwendung" marke="Wirkung sichtbar, bevor man handelt">
                {verwendung.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">Noch nirgends verwendet.</p>
                ) : (
                  <div className="flex flex-col">
                    {verwendung.map((v, i) => {
                      const inhalt = (
                        <>
                          <span className="font-zahl text-[15px] font-medium">
                            {v.wert.toLocaleString('de-DE')}
                          </span>
                          <span className="text-sm">{v.text}</span>
                          {v.ziel && <span className="ml-auto font-zahl text-tinte-3">→</span>}
                        </>
                      );
                      const klasse = `flex items-center gap-3 px-1 py-3 ${i < verwendung.length - 1 ? 'border-b border-linie' : ''}`;
                      return v.ziel ? (
                        <Link key={v.text} to={v.ziel} className={`${klasse} hover:bg-flaeche-2`}>
                          {inhalt}
                        </Link>
                      ) : (
                        <div key={v.text} className={klasse}>
                          {inhalt}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Karte>
            )}

            {!istNeu && (
              <Karte titel="Verlauf" marke="aus dem Protokoll, neueste zuerst">
                {verlauf.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">
                    Noch keine Einträge im Protokoll.
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {verlauf.map((z, i) => (
                      <div
                        key={`${z.wann}-${i}`}
                        className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                          i < verlauf.length - 1 ? 'border-b border-linie' : ''
                        }`}
                      >
                        <span className="font-zahl text-xs text-tinte-3">
                          {new Date(z.wann).toLocaleString('de-DE', {
                            timeZone: 'Europe/Berlin',
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className="text-tinte-2">{z.wer}</span>
                        <span className="max-sm:col-span-2">{z.was}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Karte>
            )}

            {!istNeu && (
              <details className="text-[12.5px] text-tinte-3">
                <summary className="cursor-pointer font-zahl text-[11px] uppercase tracking-[0.13em]">
                  Wo ist der alte Regel-Editor?
                </summary>
                <p className="mt-1.5">
                  Es gibt nur noch diesen einen Ort. Der frühere zweite Editor
                  (Erfassungsregeln-Seiten) und die Regel-Matrix der alten Strecken-Seite sind
                  ersetzt — gleiche Daten, gleiche Wirkung an den Stationen.
                </p>
              </details>
            )}
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
